import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Search, LogOut, ArrowUpRight } from "lucide-react";
import { authApi, STATUS_LABELS, STATUS_COLORS, FACILITY_LABELS, BUDGET_LABELS } from "@/lib/auth";
import { LogoMark } from "@/components/layout/Navbar";

const FILTERS = ["all", "new", "qualified", "quote-in-progress", "quote-sent", "awaiting-response", "approved", "completed"];

export function AdminHeader({ user }) {
  const navigate = useNavigate();
  const logout = async () => {
    try { await authApi.logout(); } catch {}
    navigate("/admin", { replace: true });
  };
  return (
    <header className="border-b hairline bg-[#1C2220]/85 backdrop-blur-xl sticky top-0 z-40">
      <div className="px-4 sm:px-8 max-w-7xl mx-auto flex items-center justify-between h-16">
        <Link to="/admin/dashboard" className="flex items-center gap-3" data-testid="admin-home-link">
          <LogoMark />
          <span className="font-display font-bold tracking-[0.18em] text-xs">FAIRLUME <span className="text-[#A7D7A0]">ADMIN</span></span>
        </Link>
        <div className="flex items-center gap-5">
          <span className="hidden sm:block text-xs text-white/40" data-testid="admin-user-email">{user?.email}</span>
          <Link to="/admin/suppliers" className="text-xs text-white/40 hover:text-white transition-colors" data-testid="admin-suppliers-link">Suppliers</Link>
          <Link to="/" className="text-xs text-white/40 hover:text-white transition-colors" data-testid="admin-view-site">View Site</Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.user) return;
    authApi.me()
      .then((u) => { setUser(u); setAuthState(true); })
      .catch(() => { setAuthState(false); navigate("/admin", { replace: true }); });
  }, [location.state, navigate]);

  useEffect(() => {
    if (!authState) return;
    setLoading(true);
    const params = {};
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    Promise.all([authApi.stats(), authApi.leads(params)])
      .then(([s, l]) => { setStats(s); setLeads(l.leads); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authState, status, q]);

  const cards = useMemo(() => stats && [
    { label: "Total Leads", value: stats.total, testid: "stat-total" },
    { label: "New", value: stats.by_status.new, testid: "stat-new" },
    { label: "Active Pipeline", value: stats.active_pipeline, testid: "stat-pipeline" },
    { label: "Quotes Sent", value: stats.by_status["quote-sent"], testid: "stat-quotes" },
    { label: "Approved", value: stats.by_status.approved, testid: "stat-approved" },
    { label: "Completed", value: stats.by_status.completed, testid: "stat-completed" },
  ], [stats]);

  if (authState === null) {
    return <main className="min-h-screen bg-void flex items-center justify-center"><p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-white/40">Loading…</p></main>;
  }
  if (!authState) return null;

  return (
    <main className="min-h-screen bg-void" data-testid="admin-dashboard">
      <AdminHeader user={user} />
      <div className="px-4 sm:px-8 max-w-7xl mx-auto py-10">
        {cards && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5 rounded-md overflow-hidden mb-10">
            {cards.map((c) => (
              <div key={c.label} className="bg-[#101214] p-5" data-testid={c.testid}>
                <p className="font-display font-bold text-2xl sm:text-3xl text-white">{c.value}</p>
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1.5">{c.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              data-testid="admin-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, company, email, location…"
              className="w-full bg-white/[0.04] border hairline rounded-sm pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#A7D7A0]/60"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`admin-filter-${f}`}
                onClick={() => setStatus(f)}
                className={`px-3.5 py-2 rounded-sm border text-xs transition-all ${
                  status === f ? "border-[#A7D7A0] bg-[#A7D7A0]/10 text-white" : "hairline text-white/50 hover:text-white"
                }`}
              >
                {f === "all" ? "All" : STATUS_LABELS[f]}
              </button>
            ))}
          </div>
        </div>

        <div className="border hairline rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b hairline text-left">
                  <th className="px-5 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40">Company</th>
                  <th className="px-5 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40">Contact</th>
                  <th className="px-5 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40 hidden md:table-cell">Facility</th>
                  <th className="px-5 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40 hidden lg:table-cell">Budget</th>
                  <th className="px-5 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40">Status</th>
                  <th className="px-5 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40 hidden sm:table-cell">Received</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.lead_id}
                    data-testid={`lead-row-${l.lead_id}`}
                    onClick={() => navigate(`/admin/leads/${l.lead_id}`)}
                    className="border-b hairline last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-4 text-white font-medium">{l.company}</td>
                    <td className="px-5 py-4 text-white/60">{l.full_name}</td>
                    <td className="px-5 py-4 text-white/60 hidden md:table-cell">{FACILITY_LABELS[l.facility_type] || l.facility_type}</td>
                    <td className="px-5 py-4 text-white/60 hidden lg:table-cell">{BUDGET_LABELS[l.budget] || l.budget}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-sm border text-[11px] ${STATUS_COLORS[l.status] || STATUS_COLORS.new}`} data-testid={`lead-status-${l.lead_id}`}>
                        {STATUS_LABELS[l.status] || l.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-white/40 text-xs hidden sm:table-cell">
                      {new Date(l.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </td>
                    <td className="px-5 py-4 text-right"><ArrowUpRight size={14} className="text-white/30 inline" /></td>
                  </tr>
                ))}
                {!loading && leads.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-14 text-center text-white/30 text-sm" data-testid="leads-empty">No leads match this view.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
}
